import { useEffect, useState } from "react";
import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { motion } from "framer-motion";
import { ArrowSquareLeft, ArrowSquareRight } from "iconsax-reactjs";
import { LuHeartHandshake } from "react-icons/lu";
import * as Sentry from "@sentry/react";
import { BookSessionErrorFallback } from "@/components/BookSessionErrorFallback";
import { ClientInformationForm } from "@/components/ClientInformationForm";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import TrainerNavbar from "@/components/trainer/TrainerNavbar";
import { TrainerGreeting } from "@/components/trainer/TrainerGreeting";
import { Overlay } from "@/components/ui/overlay";
import { useLocale } from "@/contexts/IntlContext";
import { useForcedTheme } from "@/contexts/ThemeContext";
import { useAuthStore } from "@/lib/stores/authStore";
import { useCourseProgressStore } from "@/lib/stores/courseProgressStore";

export const Route = createFileRoute("/trainer")({
  component: TrainerLayout,
});

function TrainerLayout() {
  useForcedTheme("adult");
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const { locale } = useLocale();
  const isRTL = locale === "ar";
  const user = useAuthStore((s) => s.user);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const hydrateProgress = useCourseProgressStore((s) => s.hydrateFromBackend);
  const reconcileProgressOwner = useCourseProgressStore(
    (s) => s.reconcileOwner,
  );

  const pathname = location.pathname;
  const isPlayer = pathname.endsWith("/player");
  const isAuth = pathname.startsWith("/trainer/auth");
  const isProfile = pathname.startsWith("/trainer/profile");
  const isNotesHome =
    pathname === "/trainer/notes" || pathname === "/trainer/notes/";
  const isCourseNotes = pathname.startsWith("/trainer/notes/") && !isNotesHome;
  const isTopLevel =
    pathname === "/trainer/courses" ||
    pathname === "/trainer/courses/" ||
    isProfile;


  const backTo = isCourseNotes
    ? "/trainer/notes"
    : isNotesHome
      ? "/trainer/profile"
      : "/trainer/courses";

  const bare = isPlayer || isAuth;
  const showNavbar = !bare;
  const showBack = !isTopLevel && !bare;
  const showLanguage = isTopLevel;
  const showGreeting = isTopLevel && !isProfile;
  const showSupportButton = isTopLevel;

  useEffect(() => {
    reconcileProgressOwner();
    hydrateProgress();
  }, [user?.accountType, hydrateProgress, reconcileProgressOwner]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("hamd:chatbot-visibility", {
        detail: { hidden: isSupportOpen },
      }),
    );
    return () => {
      window.dispatchEvent(
        new CustomEvent("hamd:chatbot-visibility", {
          detail: { hidden: false },
        }),
      );
    };
  }, [isSupportOpen]);

  if (isAuth) {
    return <Outlet />;
  }

  return (
    <div className={`min-h-screen ${showNavbar ? "pb-24" : "pb-0"}`}>
      {!isPlayer && (
        <div className="flex items-center justify-between p-4 pt-6 h-20">
          <div className="flex h-9 min-w-0 flex-1 items-center">
            {showGreeting && <TrainerGreeting />}
            {showBack && (
              <button
                onClick={() => navigate({ to: backTo })}
                aria-label={intl.formatMessage({ id: "common.back" })}
                className="text-primary"
              >
                {isRTL ? (
                  <ArrowSquareRight variant="Bold" size={36} strokeWidth={3.5} />
                ) : (
                  <ArrowSquareLeft variant="Bold" size={36} strokeWidth={3.5} />
                )}
              </button>
            )}
          </div>
          {showLanguage && (
            <LanguageSwitcher className="w-9 h-9 shrink-0 ms-3" />
          )}
        </div>
      )}

      <div
        className={
          isPlayer
            ? "h-dvh overflow-hidden"
            : "overflow-y-auto h-[calc(100dvh-5rem)] min-h-0"
        }
      >
        <Outlet />
      </div>

      {showNavbar && (
        <TrainerNavbar
          activeTab={isProfile || isNotesHome || isCourseNotes ? "profile" : "courses"}
        />
      )}

      {showSupportButton && (
        <div className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] right-0.5 md:bottom-2 md:right-2 z-10">
          <motion.div
            className="overflow-hidden w-21 relative transition-colors"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <motion.button
              onClick={() => setIsSupportOpen(true)}
              className="w-full px-1 py-2 flex flex-col items-center justify-center"
              animate={{
                scale: [1, 1.08, 1],
                transition: {
                  duration: 2.4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  repeatDelay: 0.6,
                },
              }}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.94 }}
              transition={{ duration: 0.3 }}
            >
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mb-1 shadow-lg">
                <LuHeartHandshake size={24} className="text-white" />
              </div>
              <div className="text-[9px] font-medium text-white text-center leading-tight bg-primary p-1.5 rounded-lg shadow-lg">
                <FormattedMessage
                  id="trainer.support.button"
                  values={{ br: <br /> }}
                />
              </div>
            </motion.button>
          </motion.div>
        </div>
      )}

      <Overlay show={isSupportOpen} onClose={() => setIsSupportOpen(false)}>
        <Sentry.ErrorBoundary
          fallback={({ resetError }) => (
            <BookSessionErrorFallback
              onClose={() => {
                resetError();
                setIsSupportOpen(false);
              }}
            />
          )}
        >
          <ClientInformationForm
            onClose={() => setIsSupportOpen(false)}
            variant="training"
          />
        </Sentry.ErrorBoundary>
      </Overlay>
    </div>
  );
}
